import { UseQueryResult } from '@tanstack/react-query';
import { ODataResponse, ParsedODataResponse } from '../types/types';
import { DsnPaiement, DsnPaymentsResponse } from '../types/paiement.types';
import { DsnBilan, DsnBilansResponse } from '../types/bilan.types';
import { DsnDetailsData, DsnDetailsResponse } from '../types/dsn.types';

// ✅ Wrapper générique avec gestion d'erreur
type ParsedQueryResult<T> =
  { status: 'success'; data: T } | { status: 'error'; error: Error } | { status: 'loading' };

export function parseQueryDsnsResponse<T>(
  queryResult: UseQueryResult<ODataResponse<T>, Error>,
): ParsedODataResponse<T> {
  if (!queryResult.data) {
    return { data: { value: [], '@odata.count': 0 } };
  }

  const data: ODataResponse<T> = {
    value: Array.isArray(queryResult.data.value) ? queryResult.data.value : [],
    '@odata.count':
      typeof queryResult.data['@odata.count'] === 'number' ? queryResult.data['@odata.count'] : 0,
  };

  return { data };
}

export function parseQueryDsnDetailResponse(
  queryResult: UseQueryResult<DsnDetailsResponse, Error>,
): {
  data: DsnDetailsRaw | Record<string, never>;
} {
  const detailData = queryResult.data;

  // Check if value array is empty
  const isEmptyResponse = !detailData?.value || detailData.value.length === 0;
  const dsnDetails = isEmptyResponse ? {} : detailData.value[0];

  return {
    data: dsnDetails,
  };
}

export function parseQueryBilanDetailResponse(
  queryResult: UseQueryResult<DsnBilansResponse, Error>,
): {
  data: DsnBilan | Record<string, never>;
} {
  const detailData = queryResult.data;

  // Check if value array is empty
  const isEmptyResponse = !detailData?.value || detailData.value.length === 0;
  const bilanDetails = isEmptyResponse ? {} : detailData.value[0];

  return {
    data: bilanDetails,
  };
}

// ✅ Fonction générique
function parseODataListResponse<T>(
  queryResult: UseQueryResult<ODataResponse<T>, Error>,
): ParsedQueryResult<{ items: T[]; totalCount: number }> {
  if (queryResult.isError) {
    return { status: 'error', error: queryResult.error };
  }
  if (queryResult.isLoading) {
    return { status: 'loading' };
  }

  const responseData = queryResult.data;
  const items: T[] = responseData?.value ?? [];
  const totalCount = responseData?.['@odata.count'] ?? 0;

  return { status: 'success', data: { items, totalCount } };
}

export function parseQueryBilansResponse(
  queryResult: UseQueryResult<DsnBilansResponse, Error>,
): ParsedQueryResult<{ items: DsnBilan[]; totalCount: number }> {
  const result = parseODataListResponse(queryResult);
  return result;
}

export function parseQueryPaymentsResponse(
  queryResult: UseQueryResult<DsnPaymentsResponse, Error>,
): ParsedQueryResult<{ items: DsnPaiement[]; totalCount: number }> {
  const result = parseODataListResponse(queryResult);
  return result;
}

interface DsnDetailsRaw {
  DsnTiersDeclarantObj?: {
    RaisonSociale?: string;
    Siret?: string;
  };
  DsnTiersDeclareObj?: {
    RaisonSociale?: string;
    Siret?: string;
  };
  DsnEnvoiSageDirectObj?: {
    InstanceIdSageDirect?: string;
    Version?: string;
    Test?: boolean;
  };
  SalarieArretTravail?: string;
  Nature?: string;
  CodeTypeDeclaration?: string;
  Periode?: string;
  DateCreationInterchange?: string;
  LastModificationStamp?: string;
  Status?: string;
}

// ✅ Validation runtime + type guard
function isDsnDetailsComplete(details: DsnDetailsRaw): boolean {
  return !!(
    details.DsnTiersDeclarantObj?.Siret &&
    details.DsnTiersDeclareObj?.Siret &&
    details.Status
  );
}

export function buildDsnDetails(dsnDetails: DsnDetailsRaw): DsnDetailsData {
  if (!isDsnDetailsComplete(dsnDetails)) {
    throw new Error('DSN details incomplete: missing required fields');
  }

  const dsnData: DsnDetailsData = {
    declarant: {
      raisonSociale: dsnDetails.DsnTiersDeclarantObj?.RaisonSociale ?? '',
      siret: dsnDetails.DsnTiersDeclarantObj?.Siret ?? '',
    },
    declare: {
      raisonSociale: dsnDetails.DsnTiersDeclareObj?.RaisonSociale ?? '',
      siret: dsnDetails.DsnTiersDeclareObj?.Siret ?? '',
    },
    declaration: {
      numeroEnvoi: dsnDetails.DsnEnvoiSageDirectObj?.InstanceIdSageDirect ?? '',
      salarie: dsnDetails.SalarieArretTravail,
      nature: dsnDetails.Nature ?? '',
      type: dsnDetails.CodeTypeDeclaration ?? '',
      phase: dsnDetails.DsnEnvoiSageDirectObj?.Version ?? '',
      modeEnvoi: dsnDetails.DsnEnvoiSageDirectObj?.Test ?? false,
      periode: dsnDetails.Periode ?? '',
      deposeLe: dsnDetails.DateCreationInterchange ?? '',
      actualiseLe: dsnDetails.LastModificationStamp ?? '',
      statut: dsnDetails.Status ?? '',
    },
  };

  return dsnData;
}
